import { preventDefault, hash_key } from "tom-select/src/utils.ts";
import { getDom } from "tom-select/src/vanilla.ts";

export default function (userOptions) {
  let self = this;
  let orig_onOptionSelect = self.onOptionSelect;
  let hasOpened = false;

  self.settings.hideSelected = false;

  const cbOptions = Object.assign(
    {
      // so that the user may add different ones as well
      className: "tomselect-checkbox",

      // the following default to the historic plugin's values
      checkedClassNames: undefined,
      uncheckedClassNames: undefined,
    },
    userOptions,
  );

  var updateChecked = function (checkbox, toCheck) {
    if (toCheck) {
      checkbox.checked = true;
      if (cbOptions.uncheckedClassNames) {
        checkbox.classList.remove(...cbOptions.uncheckedClassNames);
      }
      if (cbOptions.checkedClassNames) {
        checkbox.classList.add(...cbOptions.checkedClassNames);
      }
    } else {
      checkbox.checked = false;
      if (cbOptions.checkedClassNames) {
        checkbox.classList.remove(...cbOptions.checkedClassNames);
      }
      if (cbOptions.uncheckedClassNames) {
        checkbox.classList.add(...cbOptions.uncheckedClassNames);
      }
    }
  };

  // update the checkbox for an option
  var updateCheckbox = function (option) {
    setTimeout(() => {
      var checkbox = option.querySelector("input." + cbOptions.className);
      if (checkbox instanceof HTMLInputElement) {
        updateChecked(checkbox, option.classList.contains("selected"));
        updateCheckboxIndeterminate(option);
      }
    }, 1);
  };

  var updateCheckboxIndeterminate = function (option) {
    let checkbox = getOptionCheckbox(option);
    let group = option.getAttribute("group");
    if (option.hasAttribute("is_parent")) {
      if (noChildOptionsSelected(group)) {
        // If we somehow end up with no children selected, but the parent is,
        // we need to remove the parent from the list.
        checkbox.indeterminate = false;
        self.removeItem(option.dataset.value);
      } else if (allChildOptionsSelected(group)) {
        // If all children are selected, the parent is now determinate.
        checkbox.indeterminate = false;
      } else {
        checkbox.indeterminate = true;
      }
    }
  };

  let allChildOptionsSelected = function (group) {
    let children = getChildOptions(group);
    let selected_children = getSelectedChildOptionsForGroup(group);
    return selected_children.length === children.length;
  };

  let noChildOptionsSelected = function (group) {
    let selected_children = getSelectedChildOptionsForGroup(group);
    return selected_children.length === 0;
  };

  let getOptionCheckbox = function (option) {
    return option.querySelector("input." + cbOptions.className);
  };

  let getParentOption = function (option) {
    let group = option.getAttribute("group");
    return document.querySelector(`[group="${group}"][is_parent].option`);
  };

  let getChildOptions = function (group) {
    return Object.values(self.options).filter(
      (option) => option.group === group && !option.is_parent,
    );
  };

  let getChildItems = function (group_name) {
    let child_items = getChildItemsForGroup(group_name);
    return child_items;
  };

  let getChildItemsForGroup = function (group) {
    let child_items = document.querySelectorAll(
      `[data-ts-item][group="${group}"]:not([is_parent])`,
    );
    return child_items;
  };

  let getSelectedChildOptionsForGroup = function (group) {
    return Object.values(self.options).filter(
      (option) =>
        option.group === group &&
        option.$div.className.includes("selected") &&
        !option.is_parent,
    );
  };

  let onChildOptionAdd = function (option) {
    let parent_option = getParentOption(option);
    self.addItem(parent_option.dataset.value);
    hideParentItem(option.getAttribute("group")); // The parent may already be hidden, but good to verify.

    if (
      parent_option &&
      allChildOptionsSelected(option.getAttribute("group"))
    ) {
      selectFullGroup(parent_option);
    }

    updateCheckboxIndeterminate(parent_option);
  };

  let selectFullGroup = function (parent_option) {
    let child_options = getChildOptions(parent_option.getAttribute("group"));

    child_options.forEach((child_option) => {
      self.addItem(child_option.value);
      let child_option_dom = self.getOption(child_option.value);
      if (child_option_dom) {
        child_option_dom.classList.add("selected");
      }
    });

    hideChildItems(parent_option.dataset.value);
    showParentItem(parent_option.dataset.value);
    showGroupCountIndicator(parent_option.dataset.value);
  };

  let onChildOptionRemove = function (option) {
    // Check if this was the last child option to be selected in the group.
    // If so, remove the parent option's selected class.
    let parent_option = getParentOption(option);
    if (parent_option && noChildOptionsSelected(option.getAttribute("group"))) {
      self.removeItem(parent_option.dataset.value);
    } else {
      showChildItems(option.getAttribute("group"));
      hideParentItem(option.getAttribute("group"));
    }
    self.refreshOptions();
    updateCheckboxIndeterminate(parent_option);
  };

  let onParentOptionSelect = function (parent_option) {
    if (noChildOptionsSelected(parent_option.getAttribute("group"))) {
      selectFullGroup(parent_option);
    } else {
      onParentOptionRemove(parent_option);
    }
    updateCheckboxIndeterminate(parent_option);
  };

  let onParentOptionRemove = function (parent_option) {
    let child_options = getChildOptions(parent_option.getAttribute("group"));
    child_options.forEach((child_option) => {
      self.removeItem(child_option.value);
      let child_option_dom = self.getOption(child_option.value);
      if (child_option_dom) {
        child_option_dom.classList.remove("selected");
      }
    });

    showChildItems(parent_option.dataset.value); // We're not showing them now, but we'll show them again when they're selected.
    hideGroupCountIndicator(parent_option.dataset.value);
  };

  let hideChildItems = function (group_name) {
    let child_items = getChildItems(group_name);
    child_items.forEach((child_item) => {
      child_item.classList.add("!hidden");
    });
  };

  let showChildItems = function (group_name) {
    let child_items = getChildItems(group_name);
    child_items.forEach((child_item) => {
      child_item.classList.remove("!hidden");
    });
  };

  let hideParentItem = function (group_name) {
    let parent_item = getParentItem(group_name);

    // If we already have < max selected children, the parent won't exist.
    if (parent_item) {
      parent_item.classList.add("!hidden");
    }
  };

  let showParentItem = function (group_name) {
    let parent_item = getParentItem(group_name);
    parent_item.classList.remove("!hidden");
  };

  let getParentItem = function (group_name) {
    return document.querySelector(
      `[data-ts-item][group="${group_name}"][is_parent]`,
    );
  };

  let showGroupCountIndicator = function (group_name) {
    let count_indicator = document.querySelector(
      `#${group_name}-count-indicator`,
    );
    count_indicator.classList.remove("hidden");
  };

  let hideGroupCountIndicator = function (group_name) {
    let count_indicator = document.querySelector(
      `#${group_name}-count-indicator`,
    );
    if (count_indicator) {
      count_indicator.classList.add("hidden");
    }
  };

  // add checkbox to option template
  self.hook("after", "setupTemplates", () => {
    var orig_render_option = self.settings.render.option;

    self.settings.render.option = (data, escape_html) => {
      var rendered = getDom(orig_render_option.call(self, data, escape_html));
      var checkbox = document.createElement("input");

      if (cbOptions.className) {
        checkbox.classList.add(cbOptions.className);
      }

      checkbox.addEventListener("click", function (evt) {
        preventDefault(evt);
      });

      checkbox.type = "checkbox";
      const hashed = hash_key(data[self.settings.valueField]);

      updateChecked(checkbox, !!(hashed && self.items.indexOf(hashed) > -1));

      rendered.prepend(checkbox);
      return rendered;
    };
  });

  self.hook("after", "setup", () => {
    // We only want to do this on the initial open - i.e. when we're
    // initially loading stuff. It's wasteful to do this every time the
    // dropdown opens, as we handle this stuff elsewhere.
    if (!hasOpened) {
      for (let item of self.items) {
        let option = self.options[item]; // Can't use getOption because the dropdown hasn't been opened
        if (
          option.is_parent &&
          option.selected_children.length === option.child_keys.length
        ) {
          showParentItem(option.group);
          showGroupCountIndicator(option.group);
          hideChildItems(option.group);
        }
      }
    }
  });

  self.hook("after", "open", () => {
    for (let item of self.items) {
      let option = self.options[item];
      if (option.is_parent) {
        let dom_option = self.getOption(option.value);
        updateCheckboxIndeterminate(dom_option);
      }
    }
  });

  // uncheck when item removed
  self.on("item_remove", (value) => {
    var option = self.getOption(value);

    if (option) {
      // if dropdown hasn't been opened yet, the option won't exist
      if (option.hasAttribute("is_parent")) {
        onParentOptionRemove(option);
      } else {
        onChildOptionRemove(option);
      }

      option.classList.remove("selected"); // selected class won't be removed yet
      updateCheckbox(option);
    }
  });

  // check when item added
  self.on("item_add", (value) => {
    var option = self.getOption(value);

    if (option) {
      // if dropdown hasn't been opened yet, the option won't exist
      updateCheckbox(option);
    }
  });

  // remove items when selected option is clicked
  self.hook("instead", "onOptionSelect", (evt, option) => {
    if (option.classList.contains("selected")) {
      option.classList.remove("selected");
      self.removeItem(option.dataset.value);
      self.refreshOptions();
      preventDefault(evt, true);
      return;
    }

    orig_onOptionSelect.call(self, evt, option);

    updateCheckbox(option);

    if (option.hasAttribute("is_parent")) {
      onParentOptionSelect(option);
    } else {
      onChildOptionAdd(option);
    }
  });
}
